// Probing what was handed over, and turning loose files into library clips.
// Shared by the upload page and a drop on the room's Material sidebar, so both
// read the same files the same way.

import type { NewTag } from "@shared/api-types.js";
import type { ClipToSend } from "./library-upload";
import { guessClip } from "./clip-meta";
import type { IntakeFile } from "./intake";
import { trackNames } from "./naming";
import type { AudioProbe } from "./probe";
import { probeFile } from "./to-wav";

export interface ReadFile {
  file: IntakeFile;
  probe: AudioProbe;
}

/** Probe every file; the ones that aren't readable audio come back by path. */
export async function readFiles(
  files: IntakeFile[],
  onProgress?: (done: number, total: number) => void,
): Promise<{ ok: ReadFile[]; bad: string[] }> {
  const ok: ReadFile[] = [];
  const bad: string[] = [];
  for (const [i, file] of files.entries()) {
    onProgress?.(i, files.length);
    const probe = await probeFile(file.blob, file.ext);
    if (probe) ok.push({ file, probe });
    else bad.push(file.path);
  }
  return { ok, bad };
}

/**
 * Library clips with their guesses filled in: names without the prefix a
 * pack shares, tempo from the name or the loop's length, loop or one-shot.
 */
export function clipsFromFiles(files: ReadFile[], suggestions?: ReadonlyMap<string, NewTag[]>): ClipToSend[] {
  const names = trackNames(files.map((f) => f.file.name));
  return files.map((f, i) => {
    const guess = guessClip(f.file.name, f.probe.durationSec);
    const tags = suggestions?.get(f.file.path);
    return { file: f.file, probe: f.probe, name: names[i]!, bpm: guess.bpm, kind: guess.kind, ...(tags && tags.length > 0 ? { tags } : {}) };
  });
}
